import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, loggerLink } from "@trpc/client";
import superjson from "superjson";
import { Decimal } from "decimal.js";
import { trpc } from "src/trpc";

superjson.registerCustom<Decimal, string>(
  {
    isApplicable: (v): v is Decimal => Decimal.isDecimal(v),
    serialize: (v) => v.toJSON(),
    deserialize: (v) => new Decimal(v),
  },
  "decimal.js",
);

interface Props {
  token: string | null;
  children: React.ReactNode;
}

export function TrpcProvider({ token, children }: Props) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            cacheTime: 0,
          },
        },
      }),
  );
  const trpcClient = React.useMemo(
    () =>
      trpc.createClient({
        transformer: superjson,
        links: [
          loggerLink({
            enabled: () => import.meta.env.MODE === "development",
          }),
          httpBatchLink({
            url: import.meta.env.MODE === "app" ? "https://app.nirvanahabits.com/trpc" : "/trpc",
            headers() {
              if (!token) {
                return {};
              }

              return {
                Authorization: token,
              };
            },
          }),
        ],
      }),
    [token],
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
